<template>
  <main class="receive-page">
    <AppNavBar
      :title="t('wallet.payment')"
      :border="false"
      background-color="#2667ff"
      leftArrowColor="#fff"
      text-color="#ffffff"
    />

    <section class="receive-top-bg" />

    <section class="receive-card">
      <div ref="posterRef" class="receive-poster">
        <div class="qr-card">
          <div class="qr-frame">
            <app-image src="@/assets/images/img_qrcode_frame.png" alt="" class="qr-frame__bg" />
            <app-image v-if="qrUrl" :src="qrUrl" alt="" class="qr-frame__code" />
          </div>
        </div>

        <app-image src="@/assets/images/img_qrcode_line.png" alt="" class="receive-line" />

        <h2>{{ t('wallet.receiveAddress') }}</h2>

        <div class="address-box">
          <span>{{ receiveAddress || '--' }}</span>
          <button type="button" @click="copyAddress">
            <app-image src="@/assets/images/copy_icon_gray.png" alt="" />
          </button>
        </div>

        <div class="network-tip">
          <van-icon name="warning-o" />
          <span>{{ transferInPropertyText }}</span>
        </div>
      </div>
    </section>

    <div class="receive-bottom">
      <van-button block round type="primary" @click="saveCollectionCode">
        {{ t('wallet.saveCollectionCode') }}
      </van-button>
    </div>
  </main>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useProfileStore } from '@/stores/profile';
import { copyToClipboard } from '@/utils/clipboard';

const { t } = useI18n();
const profileStore = useProfileStore();
const qrUrl = ref('');
const posterRef = ref(null);

const networkName = computed(() => profileStore.currentNetwork.name || 'BNB Smart Chain');
const receiveAddress = computed(() => profileStore.currentAddress || '0x0000000000000000000000000000000000000000');
const transferInPropertyText = computed(() => t('wallet.transferInProperty', { network: networkName.value }));

async function copyAddress() {
  const copied = await copyToClipboard(receiveAddress.value);
  if (copied) {
    showPassedToast(t('wallet.copySuccess'));
    return;
  }
  showFailToast(t('wallet.copyFailed'));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function wrapText(context, text, maxWidth) {
  const chars = String(text || '').split('');
  const lines = [];
  let current = '';

  chars.forEach((char) => {
    const next = current + char;
    if (context.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = char;
    } else {
      current = next;
    }
  });

  if (current) {
    lines.push(current);
  }

  return lines;
}

async function saveCollectionCode() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 980;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('canvas unavailable');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const [frameImage, lineImage, qrImage] = await Promise.all([
      loadImage(new URL('@/assets/images/img_qrcode_frame.png', import.meta.url).href),
      loadImage(new URL('@/assets/images/img_qrcode_line.png', import.meta.url).href),
      qrUrl.value ? loadImage(qrUrl.value) : Promise.resolve(null)
    ]);

    ctx.drawImage(frameImage, 150, 40, 420, 420);
    if (qrImage) {
      ctx.drawImage(qrImage, 215, 106, 290, 290);
    }

    ctx.drawImage(lineImage, 90, 496, 540, 16);

    ctx.fillStyle = '#000000';
    ctx.font = '700 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(t('wallet.receiveAddress'), 360, 585);

    ctx.fillStyle = '#e2edff';
    roundRect(ctx, 75, 625, 570, 110, 20);
    ctx.fill();

    ctx.fillStyle = '#333333';
    ctx.font = '28px sans-serif';
    const addressLines = wrapText(ctx, receiveAddress.value, 500);
    addressLines.slice(0, 2).forEach((line, index) => {
      ctx.fillText(line, 360, 670 + index * 34);
    });

    ctx.textAlign = 'left';
    ctx.fillStyle = '#f59e0b';
    ctx.font = '28px sans-serif';
    ctx.fillText('!', 92, 805);
    ctx.fillStyle = '#f59e0b';
    ctx.font = '24px sans-serif';
    const tipLines = wrapText(ctx, transferInPropertyText.value, 560);
    tipLines.slice(0, 2).forEach((line, index) => {
      ctx.fillText(line, 130, 805 + index * 30);
    });

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `winpay-receive-${networkName.value}.png`;
    link.click();
    showPassedToast(t('wallet.saveSuccess'));
  } catch (error) {
    showFailToast(t('wallet.saveFailed'));
  }
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

watch(receiveAddress, async (value) => {
  qrUrl.value = value ? await QRCode.toDataURL(value, { margin: 0, width: 352 }) : '';
}, { immediate: true });
</script>

<style scoped lang="scss">
.receive-page {
  min-height: var(--app-viewport-height);
  padding-bottom: calc(104px + env(safe-area-inset-bottom));
  background: #f5f5f5;
}

.receive-top-bg {
  height: 150px;
  margin-top: calc(-1 * var(--van-nav-bar-height, 46px));
  background: #2667ff;
  border-bottom-left-radius: 30px;
  border-bottom-right-radius: 30px;
}

.receive-card {
  margin: -110px 20px 0;
  padding: 0;
  border-radius: 20px;
  background: #ffffff;
}

.receive-poster {
  padding: 20px 15px 10px;
  text-align: center;
}

.qr-card {
  display: flex;
  justify-content: center;
}

.qr-frame {
  position: relative;
  width: 200px;
  height: 200px;
}

.qr-frame__bg {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-frame__code {
  position: absolute;
  inset: 24px;
  width: calc(100% - 48px);
  height: calc(100% - 48px);
}

.receive-line {
  width: 290px;
  max-width: 100%;
  margin: 20px auto 18px;
  display: block;
}

h2 {
  margin: 0;
  color: #000000;
  font-size: 18px;
  line-height: 25px;
  font-weight: 700;
}

.address-box {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 15px;
  padding: 15px;
  border-radius: 10px;
  background: #e2edff;
  gap: 6px;

  span {
    color: #333333;
    font-size: 14px;
    line-height: 20px;
    word-break: break-all;
    text-align: left;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    flex-shrink: 0;
  }

  .app-image {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}

.network-tip {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 0 15px 10px;
  padding: 5px 5px 5px;
  color: #f59e0b;
  text-align: left;

  :deep(.van-icon) {
    margin-top: 2px;
    font-size: 18px;
  }

  span {
    color: #f59e0b;
    font-size: 14px;
    line-height: 20px;
  }
}

.receive-bottom {
  position: fixed;
  right: 14px;
  // bottom: 0;
  left: 14px;
  padding: 14px 0 calc(20px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, rgba(245, 245, 245, 0) 0%, #f5f5f5 28%);

  :deep(.van-button) {
    height: 50px;
    font-size: 16px;
    font-weight: 500;
    border: 0;
    background: var(--van-primary-color, #2667ff);
  }
}
</style>
